import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import MainLayout from '../../../components/MainLayout';
import DecisionTreeQuestionNew from '../components/DecisionTreeQuestion';
import ContextQuestionNew from '../components/ContextQuestion';
import ObjectiveQuestionNew from '../components/ObjectiveQuestion';
import CaseStudyQuestionNew from '../components/CaseStudyQuestion';
import { getQuestionById } from '../../../redux/api/questionsApi';
import { Question as QuestionType, CaseStudyQuestion } from '../types';
import { parseStepIdFromSearch } from '../utils/parseStepIdFromSearch';
import { findStepIndexById } from '../components/DecisionTreeQuestion/utils/utils';
import Loading from 'components/Loading';
import { ButtonBack, Content, TopArea } from './styles';

const ViewQuestion: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { search } = useLocation();
  // Deep link do QR Code impresso: `?step={question_steps.id}` abre o estudo de
  // caso já no passo correspondente.
  const stepIdFromQuery = useMemo(() => parseStepIdFromSearch(search), [search]);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState<QuestionType | null>(null);

  useEffect(() => {
    if (id) {
      loadQuestion(parseInt(id, 10));
    }
  }, [id]);

  const loadQuestion = async (questionId: number) => {
    setLoading(true);
    try {
      const data = await getQuestionById(questionId);
      setQuestion(data);
    } catch (error) {
      console.error('Erro ao carregar questão:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push('/new-questions');
  };

  const renderQuestion = () => {
    if (!question) return null;

    switch (question.question_type) {
      case 'objective_questions':
        return <ObjectiveQuestionNew question={question as any} />;

      case 'decision_tree':
        return (
          <DecisionTreeQuestionNew
            question={question as any}
            allowMultipleOnlyFlowWhenNoSingle={true}
            requireFinalSubmitClick={true}
          />
        );

      case 'question_context':
        return <ContextQuestionNew question={question as any} />;

      case 'case_study': {
        const caseStudy = question as CaseStudyQuestion;
        // Passo desconhecido (PDF antigo, passo removido, id de outra questão)
        // devolve -1 e a questão abre no primeiro passo, como antes.
        const stepIndex =
          stepIdFromQuery !== undefined
            ? findStepIndexById(caseStudy.step_multiple, stepIdFromQuery)
            : -1;

        return (
          <CaseStudyQuestionNew
            question={caseStudy}
            initialStepIndex={stepIndex >= 0 ? stepIndex : undefined}
          />
        );
      }

      default:
        return <div>Tipo de questão não suportado</div>;
    }
  };

  if (loading) {
    return (
      <MainLayout title="Carregando..." className="home">
        <Loading height="100vh" />
      </MainLayout>
    );
  }

  if (!question) {
    return (
      <MainLayout title="Questão não encontrada" className="home">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Questão não encontrada</h2>
          <p>A questão solicitada não existe ou foi removida.</p>
          <button onClick={handleBack} style={{ marginTop: '20px' }}>
            Voltar para listagem
          </button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Questões" className="home">
      <TopArea>
        <Content>
          <ButtonBack onClick={handleBack}>
            <ArrowLeftOutlined /> Voltar para Questões
          </ButtonBack>
        </Content>

        {renderQuestion()}
      </TopArea>
    </MainLayout>
  );
};

export default ViewQuestion;
